import { useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileTextIcon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import useDebounced from '@mds/shared/hooks/use-debounced';
import {
  JOB_APPLICATION_STATUSES,
  WORK_FIELDS,
  type AdminJobApplication,
  type JobApplicationStatus,
  type WorkField,
} from '@mds/shared/types/job-application';
import Select from '@mds/shared/ui/select';

import * as applications from '@/applications/applications.api';
import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import StatCard from '@/components/ui/stat-card';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import {
  NEXT_STEP,
  applicationStatusLabel,
  applicationStatusTone,
  fileSize,
  isOpenApplication,
  phoneLabel,
} from '@/domain/applications';

const ALL = 'all';
const PAGE_SIZE = 10;

const SORTS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'fullName:asc', label: 'Name' },
  { value: 'reference:asc', label: 'Reference' },
];

/** The four tiles above the list. Each one filters the list to its status. */
const TILES: { status: JobApplicationStatus; note: string }[] = [
  { status: 'submitted', note: 'Not opened yet' },
  { status: 'in_review', note: 'Being read' },
  { status: 'shortlisted', note: 'To interview' },
  { status: 'hired', note: 'Joined the crew' },
];

/**
 * Applications sent from the public "Join the crew" form.
 *
 * Each one is read, then shortlisted or rejected, then hired. Notes are staff
 * only.
 */
export default function ApplicationsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('applications.manage');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>(ALL);
  const [workField, setWorkField] = useState<string>(ALL);
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  const [sort, setSort] = useState('createdAt:desc');
  const [sortKey, direction] = sort.split(':') as [
    'createdAt' | 'fullName' | 'reference',
    'asc' | 'desc',
  ];

  const [openId, setOpenId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['applications', { page, status, workField, settled, sort }],
    queryFn: () =>
      applications.listApplications({
        page,
        limit: PAGE_SIZE,
        status: status === ALL ? undefined : (status as JobApplicationStatus),
        workField: workField === ALL ? undefined : (workField as WorkField),
        search: settled || undefined,
        sort: sortKey,
        direction,
      }),
  });

  const counts = useQuery({
    queryKey: ['applications', 'counts'],
    queryFn: applications.getApplicationCounts,
  });

  const detail = useQuery({
    queryKey: ['application', openId],
    queryFn: () => applications.getApplication(openId as string),
    enabled: openId !== null,
  });
  const application = detail.data ?? null;

  const rows = list.data?.data ?? [];
  const filtered = Boolean(settled) || status !== ALL || workField !== ALL;

  const filter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPage(1);
  };

  /** Runs one change; the returned application replaces the open one. */
  const act = async (run: (a: AdminJobApplication) => Promise<AdminJobApplication>) => {
    if (!application) return false;
    setBusy(true);
    setProblem(null);
    try {
      const next = await run(application);
      queryClient.setQueryData(['application', next.id], next);
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      return true;
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not go through. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (await act((a) => applications.setApplicationStatus(a.id, 'rejected', reason))) {
      setRejecting(false);
      setReason('');
    }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    if (await act((a) => applications.addApplicationNote(a.id, note))) setNote('');
  };

  const open = (a: AdminJobApplication) => {
    setProblem(null);
    setNote('');
    setOpenId(a.id);
  };

  const next = application ? NEXT_STEP[application.status] : undefined;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {TILES.map((t) => (
          <StatCard
            key={t.status}
            density="compact"
            label={applicationStatusLabel(t.status)}
            value={counts.data ? String(counts.data[t.status]) : '–'}
            note={t.note}
            onClick={() => filter(setStatus)(status === t.status ? ALL : t.status)}
            actionLabel={`Show ${applicationStatusLabel(t.status).toLowerCase()} applications`}
          />
        ))}
      </div>

      <Card
        title={list.data ? `${list.data.pagination.total} applications` : 'Applications'}
        action={
          <Toolbar>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Reference, name, email, phone"
              aria-label="Search applications"
              maxLength={160}
              className={controlCls + ' w-[260px]'}
            />
            <Select
              value={status}
              onChange={filter(setStatus)}
              options={[
                { value: ALL, label: 'Every status' },
                ...JOB_APPLICATION_STATUSES.map((s) => ({
                  value: s,
                  label: applicationStatusLabel(s),
                })),
              ]}
              aria-label="Filter by status"
            />
            <Select
              value={workField}
              onChange={filter(setWorkField)}
              options={[
                { value: ALL, label: 'Every field' },
                ...WORK_FIELDS.map((f) => ({ value: f, label: f })),
              ]}
              aria-label="Filter by field of work"
            />
            <Select value={sort} onChange={filter(setSort)} options={SORTS} aria-label="Sort" />
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading applications…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load applications"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? 'No application matches' : 'No applications yet'}
            hint={
              filtered
                ? 'Try a different search or filter.'
                : 'People apply from the "Join the crew" form on the public site.'
            }
          />
        ) : (
          <Table head={['Reference', 'Applicant', 'Phone', 'Field', 'CV', 'Status', 'Received']}>
            {rows.map((a) => (
              <Row key={a.id} onClick={() => open(a)}>
                <Cell className="font-mono text-md">{a.reference}</Cell>
                <Cell>
                  <div className="flex flex-col">
                    <span className="font-semibold">{a.fullName}</span>
                    <span className="text-md text-ink-muted">{a.email}</span>
                  </div>
                </Cell>
                <Cell className="font-mono text-md whitespace-nowrap">{phoneLabel(a.phone)}</Cell>
                <Cell>{a.workField}</Cell>
                <Cell>
                  <span className="inline-flex items-center gap-1.5 font-mono text-md text-ink-secondary uppercase">
                    <FileTextIcon aria-hidden="true" className="size-4" />
                    {cvKind(a.cv.mimeType)}
                  </span>
                </Cell>
                <Cell>
                  <Chip className={'border ' + applicationStatusTone(a.status)}>
                    {applicationStatusLabel(a.status)}
                  </Chip>
                </Cell>
                <Cell className="font-mono text-md">{stamp(a.createdAt)}</Cell>
              </Row>
            ))}
          </Table>
        )}
        <Pager pagination={list.data?.pagination} onPage={setPage} />
      </Card>

      {/* --------------------------------------------------------- detail --- */}
      <Modal
        open={openId !== null && !rejecting}
        onClose={() => setOpenId(null)}
        title={application ? `${application.reference} · ${application.fullName}` : 'Application'}
        footer={
          application && mayManage ? (
            <>
              {next && (
                <Button
                  variant={next.to === 'hired' ? 'mint' : 'dark'}
                  disabled={busy}
                  onClick={() => void act((a) => applications.setApplicationStatus(a.id, next.to))}
                >
                  {next.label}
                </Button>
              )}
              {isOpenApplication(application.status) && (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => {
                    setReason('');
                    setProblem(null);
                    setRejecting(true);
                  }}
                >
                  Reject
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {detail.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the application…</p>
        ) : detail.isError || !application ? (
          <Banner>
            {detail.error instanceof ApiError
              ? detail.error.message
              : 'That application could not be read.'}
          </Banner>
        ) : (
          <>
            {problem && <Banner>{problem}</Banner>}
            <ApplicationDetail
              application={application}
              note={note}
              onNote={setNote}
              onSaveNote={() => void saveNote()}
              busy={busy}
              mayManage={mayManage}
            />
          </>
        )}
      </Modal>

      {/* --------------------------------------------------------- reject --- */}
      <Modal
        open={rejecting && application !== null}
        onClose={() => !busy && setRejecting(false)}
        title="Reject this application"
        footer={
          <>
            <Button variant="danger" disabled={busy} onClick={() => void reject()}>
              {busy ? 'Rejecting…' : 'Reject application'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setRejecting(false)}>
              Back
            </Button>
          </>
        }
      >
        {problem && <Banner>{problem}</Banner>}
        <p className="m-0 text-base">
          This is final. {application?.fullName} drops out of the open list; the application and CV
          stay on file.
        </p>
        <Field label="Reason" hint="Staff only. Kept on the application's history.">
          <textarea
            rows={3}
            className={inputCls}
            value={reason}
            maxLength={1000}
            placeholder="Not the right fit for the current opening."
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Modal>
    </div>
  );
}

const cvKind = (mime: string) => (mime === 'application/pdf' ? 'PDF' : 'Word');

function ApplicationDetail({
  application: a,
  note,
  onNote,
  onSaveNote,
  busy,
  mayManage,
}: {
  application: AdminJobApplication;
  note: string;
  onNote: (v: string) => void;
  onSaveNote: () => void;
  busy: boolean;
  mayManage: boolean;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <Chip className={'border ' + applicationStatusTone(a.status)}>
          {applicationStatusLabel(a.status)}
        </Chip>
        <Chip>{a.workField}</Chip>
      </div>

      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
        <Line label="Name">{a.fullName}</Line>
        <Line label="Email">
          <a
            className="text-brand-text underline-offset-2 hover:underline"
            href={`mailto:${a.email}`}
          >
            {a.email}
          </a>
        </Line>
        <Line label="Phone">
          <a className="text-brand-text underline-offset-2 hover:underline" href={`tel:${a.phone}`}>
            {phoneLabel(a.phone)}
          </a>
        </Line>
        <Line label="Field">{a.workField}</Line>
        <Line label="Received">{stamp(a.createdAt)}</Line>
      </dl>

      <section className="flex flex-col gap-3">
        <Heading>CV</Heading>
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-hairline bg-raised px-4 py-3">
          <span className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface font-mono text-sm font-semibold text-ink-secondary uppercase">
            {cvKind(a.cv.mimeType)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-base font-semibold">{a.cv.fileName}</span>
            <span className="font-mono text-sm text-ink-muted">{fileSize(a.cv.sizeBytes)}</span>
          </span>
          {a.cv.url ? (
            <a
              href={a.cv.url}
              target="_blank"
              rel="noreferrer"
              className="text-base font-semibold text-brand-text underline-offset-2 hover:underline"
            >
              Open CV
            </a>
          ) : (
            <span className="font-mono text-sm text-ink-muted uppercase">CV unavailable</span>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <Heading>Staff notes</Heading>
        {a.notes.length === 0 ? (
          <p className="m-0 text-base text-ink-muted">No notes yet.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {a.notes.map((n) => (
              <li key={n.id} className="flex flex-col gap-0.5 rounded-xl bg-raised px-4 py-3">
                <span className="text-base whitespace-pre-line">{n.body}</span>
                <span className="font-mono text-sm text-ink-muted">
                  {n.authorName} · {stamp(n.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {mayManage && (
          <div className="flex flex-col gap-2">
            <textarea
              rows={2}
              className={inputCls}
              value={note}
              maxLength={1000}
              placeholder="Add a note for the team"
              aria-label="New note"
              onChange={(e) => onNote(e.target.value)}
            />
            <div>
              <Button variant="quiet" disabled={busy || !note.trim()} onClick={onSaveNote}>
                Add note
              </Button>
            </div>
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <Heading>History</Heading>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {[...a.history].reverse().map((h) => (
            <li key={h.id} className="flex flex-col gap-0.5 border-l-2 border-hairline pl-4">
              <span className="text-base font-semibold">
                {h.fromStatus ? `${applicationStatusLabel(h.fromStatus)} → ` : 'Submitted · '}
                {h.fromStatus ? applicationStatusLabel(h.toStatus) : 'from the careers form'}
              </span>
              <span className="font-mono text-sm text-ink-muted">
                {stamp(h.occurredAt)}
                {h.actorName && ` · ${h.actorName}`}
              </span>
              {h.note && <span className="text-base text-ink-secondary">{h.note}</span>}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
      {children}
    </h3>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm text-ink-muted uppercase">{label}</dt>
      <dd className="m-0 break-words">{children}</dd>
    </>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
    >
      {children}
    </p>
  );
}
